import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { resolveBuildId, userDisplayName, useActiveOrg } from "common-fe";
import { useScoreApi } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { useStationName } from "../hooks/useStationIdentity";
import { syncEngine } from "../local/sync";
import {
  buildUnsentSheetsFile,
  collectUnsent,
  countUnuploadedWork,
  resetTablet,
  unsentSheetsFilename,
} from "../local/clearMachine";
import { sendStationSnapshotConfirmed } from "../hooks/useStationSnapshotUpload";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import { saveJsonFile } from "../util/saveJsonFile";
import { resolveBuildTime } from "../util/buildTime";

// #3568 Reset tablet, run from the gear menu on Admin Options. The show PIN on
// the tablet is the authority: no admin sign-in. Unsent sheets never block it.
//
//   confirm   online, ask once ("Reset this tablet?")
//   save      offline with Unsent sheets: "Save N sheets to a file?"
//   saved     the file is on the stick: "File saved. Reset now?"
//   working   sending / erasing
//   failed    why, with nothing erased
//
// resetTablet() (local/clearMachine.js) owns the safety rule: it erases only
// after every Unsent sheet was sent (online) or saved to a file (offline).
const FAIL_COPY = {
  offline: "resetTablet.offline",
  snapshotFailed: "resetTablet.snapshotFailed",
  recordFailed: "resetTablet.recordFailed",
  fileStale: "resetTablet.fileStale",
  noShow: "resetTablet.noShow",
  unsavedEdits: "resetTablet.unsavedEdits",
  failed: "resetTablet.failed",
};

export default function ResetTabletDialog({ open, onClose, onSelectShow, unsent = 0, online = true }) {
  const { t } = useTranslation("score");
  const auth = useAuth();
  const scoreApi = useScoreApi();
  const { selectedShow } = useScoreContext();
  const { activeOrgId } = useActiveOrg();
  const stationName = useStationName(activeOrgId);
  const [step, setStep] = useState("confirm");
  const [fail, setFail] = useState(null);
  const [saved, setSaved] = useState(null);
  const startedFor = useRef(null);

  // Each open starts at the right first step for the connection it opens under.
  useEffect(() => {
    if (!open) {
      startedFor.current = null;
      return;
    }
    if (startedFor.current !== null) return;
    startedFor.current = true;
    setFail(null);
    setSaved(null);
    setStep(!online && unsent > 0 ? "save" : "confirm");
  }, [open, online, unsent]);

  const run = async (fileSaved = null) => {
    setStep("working");
    setFail(null);
    try {
      const result = await resetTablet(
        {
          syncEngine,
          removeUser: auth.removeUser,
          sendSnapshot: sendStationSnapshotConfirmed,
          // The presence row the server checks belongs to a show, so the beat carries the
          // show the tablet last had open (selectedShow is persisted across reloads).
          showId: selectedShow?.id ?? null,
          sendHeartbeat: () =>
            scoreApi.presence.postHeartbeat({
              showId: selectedShow?.id,
              instanceId: getPresenceInstanceId(),
              scribeName: userDisplayName(auth.user?.profile) || null,
              appBuild: resolveBuildId(),
              appBuiltAt: resolveBuildTime(),
            }),
          api: { record: (id, body) => scoreApi.stations.recordStationMachineClear(id, body) },
        },
        { fileSaved },
      );
      if (result.ok) return;
      if (result.reason === "needsFile") {
        setStep("save");
        return;
      }
      setFail(result);
      setStep("failed");
    } catch {
      setFail({ reason: "failed" });
      setStep("failed");
    }
  };

  const saveFile = async () => {
    try {
      // In-memory edits are neither sent nor in the file: refuse before saving one.
      const { counts } = await countUnuploadedWork();
      if (counts.unsavedEdits > 0) {
        setFail({ reason: "unsavedEdits" });
        setStep("failed");
        return;
      }
      const collected = await collectUnsent();
      const tabletId = getPresenceInstanceId();
      const file = buildUnsentSheetsFile({
        collected,
        tabletId,
        stationName,
        show: selectedShow,
        orgId: activeOrgId,
      });
      const filename = unsentSheetsFilename({ tabletId, stationName });
      const result = await saveJsonFile(filename, file);
      // Only the picker path proves the file exists. A plain download is not proof, so
      // step 2 then asks the scribe to check the stick before Reset.
      setSaved({ filename, count: file.count, items: file.items, proven: result?.proven !== false });
      setStep("saved");
    } catch {
      // Cancelled picker or a failed write: the file does not exist, so nothing is erased.
      setFail({ reason: "fileNotSaved" });
      setStep("failed");
    }
  };

  const close = () => {
    if (step === "working") return;
    onClose?.();
  };

  const body = () => {
    if (step === "working") {
      return (
        <>
          <DialogTitle>{t("resetTablet.confirmTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText data-testid="reset-tablet-working" role="status">
              {t("resetTablet.working")}
            </DialogContentText>
          </DialogContent>
        </>
      );
    }
    if (step === "save") {
      return (
        <>
          <DialogTitle>{t("resetTablet.saveTitle", { count: unsent })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("resetTablet.saveBody", { count: unsent })}</DialogContentText>
            <List dense disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
              {["saveStep1", "saveStep2", "saveStep3"].map((key) => (
                <ListItem key={key} disableGutters sx={{ display: "list-item", py: 0 }}>
                  <ListItemText primary={t(`resetTablet.${key}`)} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={close} autoFocus>{t("resetTablet.cancel")}</Button>
            <Button variant="contained" onClick={saveFile} data-testid="reset-tablet-save-file">
              {t("resetTablet.saveFile")}
            </Button>
          </DialogActions>
        </>
      );
    }
    if (step === "saved") {
      return (
        <>
          <DialogTitle>{t("resetTablet.savedTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(saved?.proven === false ? "resetTablet.savedBodyCheck" : "resetTablet.savedBody", {
                filename: saved?.filename,
                count: saved?.count ?? 0,
              })}
            </DialogContentText>
            <List dense disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
              {(saved?.proven === false ? ["savedCheck", "savedKeep", "savedErase"] : ["savedKeep", "savedErase"]).map((key) => (
                <ListItem key={key} disableGutters sx={{ display: "list-item", py: 0 }}>
                  <ListItemText primary={t(`resetTablet.${key}`)} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={close} autoFocus>{t("resetTablet.cancel")}</Button>
            <Button color="error" variant="contained" onClick={() => run(saved)} data-testid="reset-tablet-confirm">
              {t("resetTablet.confirm")}
            </Button>
          </DialogActions>
        </>
      );
    }
    if (step === "failed") {
      const reason = fail?.reason;
      return (
        <>
          <DialogTitle>{t("resetTablet.failTitle")}</DialogTitle>
          <DialogContent>
            {reason === "sendFailed" ? (
              <DialogContentText data-testid="reset-tablet-send-failed" role="alert">
                {t("resetTablet.sendFailed", { count: fail.remaining ?? 1 })}{" "}
                {fail.why ? t("resetTablet.sendFailedWhy", { why: fail.why }) : t("resetTablet.sendFailedNoWhy")}
              </DialogContentText>
            ) : reason === "fileNotSaved" ? (
              <DialogContentText data-testid="reset-tablet-file-not-saved" role="alert">
                {t("resetTablet.fileNotSaved")}
              </DialogContentText>
            ) : (
              <DialogContentText data-testid="reset-tablet-failed" role="alert">
                {t(FAIL_COPY[reason] ?? FAIL_COPY.failed)}
                {reason === "recordFailed" && fail.why ? ` ${t("resetTablet.sendFailedWhy", { why: fail.why })}` : ""}
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={close} autoFocus>{t("resetTablet.close")}</Button>
            {reason === "noShow" ? (
              <Button
                variant="contained"
                onClick={() => {
                  onClose?.();
                  onSelectShow?.();
                }}
                data-testid="reset-tablet-select-show"
              >
                {t("resetTablet.noShowSelect")}
              </Button>
            ) : reason === "fileNotSaved" || reason === "fileStale" ? (
              <Button variant="contained" onClick={saveFile} data-testid="reset-tablet-save-file">
                {t("resetTablet.saveFile")}
              </Button>
            ) : (
              <Button variant="contained" onClick={() => run()} data-testid="reset-tablet-retry">
                {t("resetTablet.tryAgain")}
              </Button>
            )}
          </DialogActions>
        </>
      );
    }
    return (
      <>
        <DialogTitle>{t("resetTablet.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("resetTablet.confirmBody")}</DialogContentText>
          <List dense disablePadding sx={{ listStyleType: "disc", pl: 3, mt: 1 }}>
            {unsent > 0 && (
              <ListItem disableGutters sx={{ display: "list-item", py: 0 }}>
                <ListItemText primary={t("resetTablet.confirmUnsent", { count: unsent })} />
              </ListItem>
            )}
            <ListItem disableGutters sx={{ display: "list-item", py: 0 }}>
              <ListItemText primary={t("resetTablet.confirmSent")} />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={close} autoFocus>{t("resetTablet.cancel")}</Button>
          <Button color="error" variant="contained" onClick={() => run()} data-testid="reset-tablet-confirm">
            {t("resetTablet.confirm")}
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth data-testid="reset-tablet-dialog">
      {body()}
    </Dialog>
  );
}
